import { useState } from 'react';
import { Plus, Search, FileCheck, Download, Eye, Calendar, FileText } from 'lucide-react';

const mockFieldTickets = [
  { id: 'FT-001', invoiceId: 'INV-1234', fileName: 'field_ticket_001.pdf', uploadedAt: '2024-12-15', size: '2.4 MB', status: 'verified' },
  { id: 'FT-002', invoiceId: 'INV-1233', fileName: 'service_report_q4.pdf', uploadedAt: '2024-12-14', size: '1.8 MB', status: 'pending' },
  { id: 'FT-003', invoiceId: 'INV-1232', fileName: 'materials_receipt.pdf', uploadedAt: '2024-12-12', size: '890 KB', status: 'verified' },
  { id: 'FT-004', invoiceId: 'INV-1231', fileName: 'work_order_445.pdf', uploadedAt: '2024-12-10', size: '3.2 MB', status: 'verified' },
  { id: 'FT-005', invoiceId: 'INV-1230', fileName: 'inspection_report.pdf', uploadedAt: '2024-12-08', size: '1.1 MB', status: 'rejected' },
];

export default function FieldTickets() {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTickets = mockFieldTickets.filter(ticket =>
    ticket.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.invoiceId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.fileName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const styles = {
      verified: 'badge-success',
      pending: 'badge-warning',
      rejected: 'badge-destructive',
    };
    return styles[status as keyof typeof styles] || 'badge-primary';
  };

  return (
    <div className="animate-slide-up">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">Field Tickets</h1>
          <p className="page-subtitle">Manage field ticket attachments for invoice documentation.</p>
        </div>
        <button className="btn-primary">
          <Plus className="w-5 h-5" />
          Upload Ticket
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search field tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-12"
          />
        </div>
      </div>

      {/* Field Tickets Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Ticket ID</th>
              <th>File Name</th>
              <th>Invoice</th>
              <th>Size</th>
              <th>Uploaded</th>
              <th>Status</th>
              <th className="w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTickets.map((ticket) => (
              <tr key={ticket.id}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FileCheck className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-medium">{ticket.id}</span>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    {ticket.fileName}
                  </div>
                </td>
                <td>
                  <span className="text-primary font-medium">{ticket.invoiceId}</span>
                </td>
                <td className="text-muted-foreground">{ticket.size}</td>
                <td className="text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(ticket.uploadedAt).toLocaleDateString()}
                  </div>
                </td>
                <td>
                  <span className={`badge ${getStatusBadge(ticket.status)}`}>
                    {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                  </span>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <button className="btn-ghost p-2" title="View">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="btn-ghost p-2" title="Download">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredTickets.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          No field tickets found matching your search.
        </div>
      )}
    </div>
  );
}
